/* STEP 7e-2 · S1 (owner "Step 7e Methodology Card V1", 30-Oct-2026 15:10 KSA) — the "Firm settings" of ONE methodology version:
   GET /methodology/{id}/settings (everyone reads); manager roles (GET /review-roles → methodology.approve = MANAGER_GATE) run
   "Suggest from the methodology" (POST …/settings/suggest — the job methodology_settings_suggest) and confirm / edit / clear each
   field (PUT …/settings/fields/{key}). A suggestion shows its value with §section · p. page, or "Not found in this methodology";
   only CONFIRMED values are used. A superseded version is frozen (read only). Text: lib/i18n/1870_s7e2_methodology_settings.js. */
import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { toErrMsg } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { fmtDateTime } from "@/lib/dt";
import { aiErrorText } from "@/lib/aiErrorText";   // Step 11c · item 1: never a provider's own error text
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function fmtPct(x) {
  const v = Number(x);
  return `${Number.isInteger(v) ? v : +v.toFixed(4)} %`;
}

export function fmtSetting(kind, v) {
  if (v == null || v === "") return "";
  if (kind === "range") return `${fmtPct(v.min).slice(0, -2)}–${fmtPct(v.max)}`;
  if (kind === "pct") return fmtPct(v);
  return String(v);
}

function Editor({ kind, value, onChange, t }) {
  if (kind === "text") {
    return <Textarea rows={3} className="text-[12px]" value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid="meth-set-edit-text" />;
  }
  if (kind === "pct") {
    return <Input type="number" step="0.01" className="h-7 w-24 text-[12px]" placeholder={t("s7e2.settings.pct")}
                  value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid="meth-set-edit-pct" />;
  }
  const v = value || {};
  return (
    <div className="flex items-center gap-1">
      <Input type="number" step="0.01" className="h-7 w-20 text-[12px]" placeholder={t("s7e2.settings.min")}
             value={v.min ?? ""} onChange={(e) => onChange({ ...v, min: e.target.value })} data-testid="meth-set-edit-min" />
      <span>–</span>
      <Input type="number" step="0.01" className="h-7 w-20 text-[12px]" placeholder={t("s7e2.settings.max")}
             value={v.max ?? ""} onChange={(e) => onChange({ ...v, max: e.target.value })} data-testid="meth-set-edit-max" />
    </div>
  );
}

const asNumber = (kind, v) => {
  if (kind === "text") return v;
  if (kind === "pct") return v === "" || v == null ? null : Number(v);
  return { min: Number(v?.min), max: Number(v?.max) };
};

export default function MethodologySettingsDialog({ version, open, onClose }) {
  const colW = useResizableColumns({ storageKey: "methodology-settings-dialog", defaults: [160, 160, 160, 100], mins: [100, 100, 100, 60] });   // Step 11d · add-on
  const { t } = useI18n();
  const [doc, setDoc] = useState(null);
  const [editing, setEditing] = useState(null);   // { key, value }
  const [busy, setBusy] = useState(false);
  const vid = version?.id;
  const load = useCallback(() => {
    if (!vid) return Promise.resolve();
    return api.get(`/methodology/${vid}/settings`, { _silent: true }).then((r) => setDoc(r.data)).catch((e) => toast.error(toErrMsg(e)));
  }, [vid]);
  useEffect(() => { if (open) { setDoc(null); setEditing(null); load(); } }, [open, load]);
  const live = ["queued", "running"].includes(doc?.last_job?.status);
  useEffect(() => {
    if (!open || !live) return undefined;
    const id = setInterval(load, 2000);
    return () => clearInterval(id);
  }, [open, live, load]);

  const suggest = async () => {
    setBusy(true);
    try { await api.post(`/methodology/${vid}/settings/suggest`); toast.success(t("s7e2.settings.suggestQueued")); await load(); }
    catch (e) { toast.error(toErrMsg(e)); }
    finally { setBusy(false); }
  };
  const put = async (key, body) => {
    setBusy(true);
    try { const r = await api.put(`/methodology/${vid}/settings/fields/${key}`, body); setDoc((d) => ({ ...d, ...r.data })); setEditing(null); }
    catch (e) { toast.error(toErrMsg(e)); }
    finally { setBusy(false); }
  };

  const order = doc?.fields_order || [];
  const kinds = doc?.field_kinds || {};
  const groups = doc?.field_groups || {};
  const run = doc?.suggest;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[90vh] max-w-5xl overflow-y-auto" data-testid="meth-settings-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm">{t("s7e2.settings.title").replace("{n}", version?.version ?? "")} · {version?.filename}</DialogTitle>
        </DialogHeader>
        {!doc ? <p className="text-xs text-muted-foreground">…</p> : (
          <div className="space-y-3 text-[12px]">
            <p className="text-muted-foreground" data-testid="meth-settings-intro">{t("s7e2.settings.intro")}</p>
            {doc.frozen && <p className="rounded bg-slate-100 p-2 text-slate-700 dark:bg-slate-800 dark:text-slate-200" data-testid="meth-settings-frozen">{t("s7e2.settings.frozen")}</p>}
            {!doc.frozen && !doc.can_edit && <p className="text-muted-foreground" data-testid="meth-settings-readonly">{t("s7e2.settings.readOnly")}</p>}
            <div className="flex flex-wrap items-center gap-2">
              {doc.can_edit && (
                <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={busy || live} onClick={suggest} data-testid="meth-settings-suggest">
                  {live ? t("s7e2.settings.running") : t("s7e2.settings.suggest")}
                </Button>
              )}
              {run && (
                <span className="text-muted-foreground" data-testid="meth-settings-lastrun">
                  {t("s7e2.settings.lastRun").replace("{at}", fmtDateTime(run.at))
                    .replace("{found}", String((run.found_by_rule_text || []).length + (run.found_by_ai || []).length))
                    .replace("{total}", String(order.length))}
                  {" · "}
                  {run.ai?.ran ? t("s7e2.settings.aiRan").replace("{n}", String((run.found_by_ai || []).length))
                    : t("s7e2.settings.aiNotRun").replace("{why}", t(`s7e2.settings.why.${run.ai?.error || "unavailable"}`))}
                </span>
              )}
              {doc.last_job?.status === "failed" && (
                <span className="text-rose-700" data-testid="meth-settings-jobfailed">{t("s7e2.settings.jobFailed").replace("{err}", aiErrorText(t, doc.last_job.error || "", { code: doc.last_job.error_code }))}</span>
              )}
            </div>
            <ResizableTable cols={colW} wrap className="w-full border text-[12px]" data-testid="meth-settings-table">
              <thead className="border-b bg-slate-50 text-[10px] uppercase text-muted-foreground dark:bg-slate-900">
                <tr>
                  <ResizableTh index={0} className="px-2 py-1 text-start">{t("s7e2.settings.colSetting")}</ResizableTh>
                  <ResizableTh index={1} className="px-2 py-1 text-start">{t("s7e2.settings.colSuggested")}</ResizableTh>
                  <ResizableTh index={2} className="px-2 py-1 text-start">{t("s7e2.settings.colInUse")}</ResizableTh>
                  <ResizableTh index={3} className="px-2 py-1"></ResizableTh>
                </tr>
              </thead>
              <tbody className="divide-y">
                {order.map((key, i) => {
                  const f = doc.fields?.[key] || {};
                  const s = f.suggestion;
                  const kind = kinds[key];
                  const newGroup = i === 0 || groups[order[i - 1]] !== groups[key];
                  return (
                    <React.Fragment key={key}>
                      {newGroup && (
                        <tr className="bg-slate-50/60 dark:bg-slate-900/40"><td colSpan={4} className="px-2 py-1 text-[11px] font-semibold">
                          {t(`s7e2.settings.group.${groups[key]}`)}
                          {kind === "text" && <span className="ms-2 font-normal text-muted-foreground">{t("s7e2.settings.guidance")}</span>}
                        </td></tr>
                      )}
                      <tr className="align-top" data-testid={`meth-set-row-${key}`}>
                        <td className="px-2 py-1.5">{t(`s7e2.settings.field.${key}`)}</td>
                        <td className="px-2 py-1.5" data-testid={`meth-set-sug-${key}`}>
                          {s?.found ? (
                            <>
                              <div className="font-medium">{fmtSetting(kind, s.value)}</div>
                              <div className="font-mono text-[10px] text-muted-foreground">
                                {t("s7e2.settings.source").replace("{s}", s.section_ref || "—").replace("{p}", String(s.page || "—"))}
                                {" · "}{s.method === "ai" ? t("s7e2.settings.byAi") : t("s7e2.settings.byRule")}
                              </div>
                            </>
                          ) : s ? <span className="italic text-muted-foreground">{t("s7e2.settings.notFound")}</span> : "—"}
                        </td>
                        <td className="px-2 py-1.5" data-testid={`meth-set-use-${key}`}>
                          {editing?.key === key ? (
                            <Editor kind={kind} value={editing.value} onChange={(v) => setEditing({ key, value: v })} t={t} />
                          ) : f.status === "confirmed" ? (
                            <>
                              <div className="font-medium text-emerald-800 dark:text-emerald-300">{fmtSetting(kind, f.value)}</div>
                              <div className="text-[10px] text-muted-foreground">
                                {t("s7e2.settings.confirmedBy").replace("{by}", f.confirmed_by || "").replace("{at}", fmtDateTime(f.confirmed_at))}
                              </div>
                              {f.edited && s?.found && (
                                <div className="text-[10px] text-amber-700" data-testid={`meth-set-edited-${key}`}>
                                  {t("s7e2.settings.edited").replace("{v}", fmtSetting(kind, s.value))}
                                </div>
                              )}
                            </>
                          ) : <span className="text-muted-foreground">{t("s7e2.settings.notConfirmed")}</span>}
                        </td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-end">
                          {doc.can_edit && (editing?.key === key ? (
                            <>
                              <Button size="sm" className="me-1 h-6 text-[11px]" disabled={busy}
                                      onClick={() => put(key, { action: "confirm", value: asNumber(kind, editing.value) })} data-testid={`meth-set-save-${key}`}>{t("s7e2.settings.save")}</Button>
                              <Button size="sm" variant="ghost" className="h-6 text-[11px]" onClick={() => setEditing(null)}>{t("s7e2.settings.cancel")}</Button>
                            </>
                          ) : (
                            <>
                              {s?.found && f.status !== "confirmed" && (
                                <Button size="sm" variant="outline" className="me-1 h-6 text-[11px]" disabled={busy}
                                        onClick={() => put(key, { action: "confirm" })} data-testid={`meth-set-confirm-${key}`}>{t("s7e2.settings.confirm")}</Button>
                              )}
                              <Button size="sm" variant="ghost" className="me-1 h-6 text-[11px]"
                                      onClick={() => setEditing({ key, value: f.status === "confirmed" ? f.value : (s?.found ? s.value : (kind === "range" ? { min: "", max: "" } : "")) })}
                                      data-testid={`meth-set-edit-${key}`}>{t("s7e2.settings.edit")}</Button>
                              {f.status === "confirmed" && (
                                <Button size="sm" variant="ghost" className="h-6 text-[11px] text-slate-500" disabled={busy}
                                        onClick={() => put(key, { action: "clear" })} data-testid={`meth-set-clear-${key}`}>{t("s7e2.settings.clear")}</Button>
                              )}
                            </>
                          ))}
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </ResizableTable>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
